/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { EvaluationUpdateAction, FormUpdatePayload } from '..';
import { History } from "history";
import { AjaxError } from "rxjs/ajax";
import { ActionType, createAction, createAsyncAction } from "typesafe-actions";
import { PatchSchema } from '../../db/stores/patches';
import { Evaluation } from "../../evaluation";
import { CREATE_EVALUATION, EVALUATION_CREATED, EVAL_PATCH_QUEUED, FAILED_PATCH_QUEUED, FETCH_EVALUATION_UPDATES_FAILURE, FETCH_EVALUATION_UPDATES_REQUEST, FETCH_EVALUATION_UPDATES_SUCCESS, LOAD_EVALUATION, LOAD_EVALUATION_COMPLETED, SAVE_EVALUATION, SAVE_EVALUATION_SUCCESS, SET_ACTIVE_EVALUATION } from "../actions";
import { CreateEvalRecord } from "./../../db/stores/evals";
import { CLEAR_ACTIVE_EVALUATION, LOAD_EVALUATIONS, LOAD_EVALUATIONS_COMPLETED } from "./../actions";
import { EvalListFilters } from '../../react/components/EvalList';

// Eval creation
export const createEval = createAction(CREATE_EVALUATION, action => (formId: string, history: History<any>) =>
  action({ formId, history })
);

interface CreateEvalRecordActionPayload extends CreateEvalRecord {
  newEval: Evaluation;
}
export const saveCreateEvalRecord = createAction(
  EVALUATION_CREATED,
  action => (record: CreateEvalRecordActionPayload) => action(record)
);

export const scanUncreatedEvals = createAction("SCAN_UNCREATED_EVALS");

// Load Evals
export const loadEvaluations = createAction(
  LOAD_EVALUATIONS,
  action => (drafts: boolean|null, limit: number = 25, offset: number = 0, type?: string, facilityId?: string, searchText?: string, filters?: EvalListFilters) => {
    const sortField = drafts ? 'modified' : 'lastPublished';
    return action({ drafts, limit, offset, type, facilityId, sortField, searchText, filters });
});

export const loadEvaluationsCompleted = createAction(
  LOAD_EVALUATIONS_COMPLETED,
  action => (payload: {evals: { [id: string]: Evaluation }, total: number}) => action(payload)
);

export const loadEvaluation = createAction(LOAD_EVALUATION, action => (evalID: string) => action(evalID));
export const loadEvaluationCompleted = createAction(LOAD_EVALUATION_COMPLETED, action => (evaluation: Evaluation) =>
  action(evaluation)
);

// Maintaining Active Eval
export const setActiveEval = createAction(SET_ACTIVE_EVALUATION, action => (id: string, history: History<any>) =>
  action({ id, history })
);
export const clearActiveEval = createAction(CLEAR_ACTIVE_EVALUATION, action => (id: string) => action(id));
export const syncEvaluations = createAction("SYNC_EVALUATIONS");

// Eval fetch

export const fetchAllEvalPatchesActions = createAsyncAction(
  "FETCH_ALL_EVALUATION_LOGS_REQUEST",
  "FETCH_ALL_EVALUATION_LOGS_COMPLETED",
  "FETCH_ALL_EVALUATION_LOGS_FAILURE"
)<string, string, AjaxError>();

export const fetchServerGeneratedPatchesActions = createAsyncAction(
    "FETCH_SERVER_GENERATED_UPDATES_REQUEST",
    "FETCH_SERVER_GENERATED_UPDATES_SUCCESS",
    "FETCH_SERVER_GENERATED_UPDATES_FAILURE"
)<{id: string, since?: string}, { id: string; lastSync: string }, AjaxError>();

export const fetchEvalPatchesActions = createAsyncAction(
  FETCH_EVALUATION_UPDATES_REQUEST,
  FETCH_EVALUATION_UPDATES_SUCCESS,
  FETCH_EVALUATION_UPDATES_FAILURE
)<string, { id: string; lastSync: string }, AjaxError>();

export type FetchEvaluationRequestPayload = {
  uuid: string;
  localId?: string;
  formType: string;
};
export const fetchEvaluation = createAsyncAction(
  "FETCH_EVALUATION_REQUEST",
  "FETCH_EVALUATION_SUCCESS",
  "FETCH_EVALUATION_FAILURE"
)<FetchEvaluationRequestPayload, Evaluation, AjaxError>();

// Sync an eval

export const syncEvaluation = createAction("SYNC_EVALUATION", action => (id: string) => action(id));

export const evalMergeConflict = createAction("EVAL_MERGE_CONFLICT", action => (id: string) => action(id));

// Eval Persist

export const saveEval = createAction(SAVE_EVALUATION, action => (params: { evalId: string; eval: Evaluation }) =>
  action(params.eval, params.evalId)
);
export const evaluationSaved = createAction(SAVE_EVALUATION_SUCCESS, action => (id: string) => action(id));

type CreateEvalPayload = {
  id: string;
  formId: string;
  formUuid: string;
  author: string;
};
export const createRemoteEval = createAsyncAction(
  "CREATE_REMOTE_EVAL_REQUEST",
  "CREATE_REMOTE_EVAL_SUCCESS",
  "CREATE_REMOTE_EVAL_FAILURE"
)<CreateEvalPayload, Evaluation, AjaxError>();

export const addedRemoteIdentifier = createAction("ADDED_REMOTE_IDENTIFIER", action => (id: string) => action(id));

// Eval Patches

export const patchQueued = createAction(EVAL_PATCH_QUEUED, action => (patch: FormUpdatePayload, evalID: string) =>
  action(patch, evalID)
);

export const failedPatchQueued = createAction(FAILED_PATCH_QUEUED, action => (evalID: string) =>
  action(evalID)
);

export const applyRemotePatches = createAction("APPLY_REMOTE_PATCHES", action => (patches: EvaluationUpdateAction[]) =>
  action(patches, patches[0].meta)
);
export type PurgeActionRequestPaylod = {
  evalId: string;
  fromPatchToPath: string;
};
export const purgePatchLogs = createAsyncAction(
  "PURGE_PATCH_LOGS_REQUEST",
  "PURGE_PATCH_LOGS_SUCCESS",
  "PURGE_PATCH_LOGS_FAILURE"
)<PurgeActionRequestPaylod, string, Error>();

export const pushPatchActions = createAsyncAction(
  "UPLOAD_EVALUATION_PATCH",
  "UPLOAD_EVALUATION_PATCH_SUCCESS",
  "UPLOAD_EVALUATION_FAILURE"
)<string, { id: string; lastSync: string | undefined, uuid?: string, since?: string }, {id: string, error: Error, httpStatus?: number}>();

/**
 * Clear evaluation removes the local copy of the evaluation from the store,
 * but does not perform any network actions.
 */
export const clearEvaluation = createAsyncAction(
  "CLEAR_EVALUATION_REQUEST",
  "CLEAR_EVALUATION_SUCCESS",
  "CLEAR_EVALUATION_FAILURE"
)<string, string, Error>();

/**
 * Delete evaluation performs a network request to delete the evaluation, which
 * may be followed by a request to clear an evalation.
 */
export const deleteEvaluation = createAsyncAction(
  "DELETE_EVALUATION_REQUEST",
  "DELETE_EVALUATION_SUCCESS",
  "DELETE_EVALUATION_FAILURE"
)<{type: string, id: string}, string, Error>();

export const checkAllDataUploaded = createAction("CHECK_ALL_EVALUATION_DATA_UPLOADED", action => (evalId: string) =>
  action(evalId)
);

export const allDataUploaded = createAction(
  "ALL_EVALUATION_DATA_UPLOADED",
  action => (evalId: string, allDataUploaded: boolean) => action(allDataUploaded, evalId)
);

// Publishing actions
export const beginPublish = createAction("BEGIN_EVAL_PUBLISH", action => (evalID: string) => action(evalID));
export const networkDownForPublish = createAction("NETWORK_NOT_AVAILABLE");
export const networkUpForPublish = createAction("NETWORK_AVAILABLE");
export const cancelPublish = createAction("CANCEL_EVAL_PUBLISH");
export const cleanupActions = createAsyncAction("PERFORM_CLEANUP", "FINISHED_CLEANUP", "FAILED_CLEANUP")<
  Evaluation,
  undefined,
  Error
>();
export const publishPromptActions = createAsyncAction(
  "PUBLISH_PROMPT_START",
  "PUBLISH_PROMPT_FINISH",
  "PUBLISH_PROMPT_FAILURE"
)<string, string, Error>();

type PublishQueryPayload = {
  uuid: string;
  evalID: string;
  formType: string;
};

export const markStuckPatchAsSynced = createAction('MARK_STUCK_PATCH_AS_SYNCED', action => (evalID: string) => action(evalID));
export const stuckPatchNotFound = createAction('STUCK_PATCH_NOT_FOUND');
export const evalNotFound = createAction('EVAL_NOT_FOUND');

// export const reportPatchFailure = createAction('REPORT_SYNC_FAILURE_FAILURE');
export const reportPatchFailureActions = createAsyncAction(
  "REPORT_SYNC_FAILURE_REQUEST",
  "REPORT_SYNC_FAILURE_SUCCESS",
  "REPORT_SYNC_FAILURE_FAILURE"
)<{evaluation: Evaluation, stuckPatch: PatchSchema}, string, string>();

export const doPublishActions = createAsyncAction(
  "PUBLISH_EVALUATION_REQUEST",
  "PUBLISH_EVALUATION_RESPONSE",
  "PUBLISH_EVALUATION_FAILURE"
)<PublishQueryPayload, Evaluation, AjaxError>();

export const setEvalFilterAction = createAction("SET_EVAL_FILTER", action => (draftMode: boolean) => action(draftMode));

export const allPatchRequestsDispatched = createAction("ALL_PATCH_REQUESTS_QUEUED");

export const syncingEvals = createAction("SYNCING_EVALS", action => (syncing: boolean) => action(syncing));

export const setSortField = createAction('SET_SORT_FIELD', action => (sortField: string) => action(sortField));
export const setSortDirection = createAction('SET_SORT_DIRECTION', action => (sortDirection: string) => action(sortDirection));

type StorageActions =
  | ActionType<typeof loadEvaluation>
  | ActionType<typeof loadEvaluationCompleted>
  | ActionType<typeof loadEvaluations>
  | ActionType<typeof loadEvaluationsCompleted>
  | ActionType<typeof createEval>
  | ActionType<typeof saveCreateEvalRecord>
  | ActionType<typeof saveEval>
  | ActionType<typeof evaluationSaved>
  | ActionType<typeof patchQueued>
  | ActionType<typeof failedPatchQueued>
  | ActionType<typeof purgePatchLogs>
  | ActionType<typeof clearEvaluation>
  | ActionType<typeof deleteEvaluation>
  | ActionType<typeof reportPatchFailureActions>
  | ActionType<typeof markStuckPatchAsSynced>
  | ActionType<typeof stuckPatchNotFound>
  | ActionType<typeof evalNotFound>;


type SyncActions =
  | ActionType<typeof fetchAllEvalPatchesActions>
  | ActionType<typeof allPatchRequestsDispatched>
  | ActionType<typeof fetchServerGeneratedPatchesActions>
  | ActionType<typeof fetchEvalPatchesActions>
  | ActionType<typeof fetchEvaluation>
  | ActionType<typeof syncEvaluation>
  | ActionType<typeof syncEvaluations>
  | ActionType<typeof syncingEvals>
  | ActionType<typeof createRemoteEval>
  | ActionType<typeof addedRemoteIdentifier>
  | ActionType<typeof scanUncreatedEvals>
  | ActionType<typeof applyRemotePatches>
  | ActionType<typeof pushPatchActions>
  | ActionType<typeof checkAllDataUploaded>
  | ActionType<typeof allDataUploaded>
  | ActionType<typeof beginPublish>
  | ActionType<typeof networkDownForPublish>
  | ActionType<typeof networkUpForPublish>
  | ActionType<typeof cancelPublish>
  | ActionType<typeof cleanupActions>
  | ActionType<typeof publishPromptActions>
  | ActionType<typeof doPublishActions>
  | ActionType<typeof reportPatchFailureActions>;

export type EvaluationActions =
  | StorageActions
  | SyncActions
  | ActionType<typeof setEvalFilterAction>
  | ActionType<typeof setActiveEval>
  | ActionType<typeof clearActiveEval>
  | ActionType<typeof setSortField>
  | ActionType<typeof setSortDirection>
  | EvaluationUpdateAction;
